import React, { useRef, useEffect } from "react";
import "./StackSection.scss";

/**
 * StackSection — wraps a section in a sticky card.
 * As the user scrolls, each card scales down slightly when the next one slides
 * in from below, creating a "deck of cards" stacking effect.
 *
 * @param {number} index     - Position in the stack (0-based). Controls z-index.
 * @param {string} className - Extra classes forwarded to the wrapper.
 */
const StackSection = ({ children, index = 0, className = "" }) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const update = () => {
      const rect = outer.getBoundingClientRect();

      if (rect.top < 0) {
        // How far has this card scrolled past the top of the viewport?
        // We transition over the first 60 % of the card's height so it feels
        // snappy rather than lingering.
        const progress = Math.min(-rect.top / (outer.offsetHeight * 0.6), 1);

        // Scale gently from 1 → 0.92
        const scale = (1 - progress * 0.08).toFixed(5);

        // Push up very slightly so it "recedes" into the background
        const ty = -(progress * 16).toFixed(2);

        inner.style.transform = `scale(${scale}) translateY(${ty}px)`;
        inner.style.transformOrigin = "top center";
      } else {
        inner.style.transform = "";
      }
    };

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    update();

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const isFirst = index === 0;

  return (
    <div
      ref={outerRef}
      className={`stack-section${
        isFirst ? " stack-section--first" : ""
      } ${className}`}
      style={{ zIndex: 10 + index }}
    >
      <div ref={innerRef} className="stack-section__inner">
        {children}
      </div>
    </div>
  );
};

export default StackSection;
